import type { ReactNode } from 'react';
import { useTranslation } from '@mypoz/i18n';
import { Icon } from '@mypoz/ui';

import { MYPOZ_SITE_URL } from '@/lib/legacy-api.js';
import { useSignedOut } from '@/lib/session.js';
import { buttonClass } from '@/lib/styles.js';
import { useAccountSession } from '../context.js';

/**
 * Shows the page only once the account is known.
 *
 * Every screen inside it reads the customer's ID, balance or payment mode, and none of them has a
 * sensible thing to show before the record arrives: a blank customer ID on the warehouse address
 * is a parcel that cannot be matched to anyone, and an invented one is worse. So instead of each
 * screen inventing its own placeholder, the page area waits here, and a failed load is a message
 * with a retry rather than a screen quietly full of wrong values.
 *
 * The header sits outside the gate and shows a dash for what it does not yet know.
 */
export function SessionGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { status, reload } = useAccountSession();
  const signedOut = useSignedOut();

  /*
    MYP-125: the vendor said the token is missing, wrong or expired — at start-up or mid-visit.
    Checked first, so a page already showing (and any dialog on it) gives way, and "Try again"
    is never offered for something only signing in again can fix.
  */
  if (signedOut) {
    return (
      <div
        role="alert"
        className="mx-auto grid max-w-md justify-items-center gap-3 py-10 text-center"
      >
        <Icon name="lock" size={32} className="text-muted" />
        <h1 className="text-xl font-semibold text-ink">{t('account.session.title')}</h1>
        <p className="text-sm text-muted">{t('account.session.body')}</p>
        <a href={MYPOZ_SITE_URL} className={buttonClass('primary', 'mt-2 px-6')}>
          {t('account.session.goToMypoz')}
        </a>
      </div>
    );
  }

  if (status === 'ready') return <>{children}</>;

  if (status === 'error') {
    return (
      <div
        role="alert"
        className="flex flex-wrap items-center gap-3 rounded-lg border border-danger/30 bg-danger/5 p-4"
      >
        <Icon name="cloud_off" size={20} className="shrink-0 text-danger" />
        <p className="min-w-0 flex-1 text-sm text-ink">{t('account.profile.failed')}</p>
        <button
          type="button"
          onClick={reload}
          className={buttonClass('secondary', 'px-4 py-1.5 text-sm')}
        >
          {t('account.profile.retry')}
        </button>
      </div>
    );
  }

  return (
    <p role="status" className="text-sm text-muted">
      {t('account.profile.loading')}
    </p>
  );
}
